import React, { useMemo, useState, useRef, useEffect } from 'react';
import { TagFacetDimension } from '../../types/sparkTypes';
import boardStyles from '../../styles/CreativeBoard.module.css';
import tagStyles from '../../styles/TagContextSelector.module.css';
import SortIcon from '@mui/icons-material/Sort';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';

type ViewMode = 'grid-large' | 'grid-small' | 'list';
type TypeFilterOption = { value: string; label: string };

type SortField = 'name' | 'publishedAt';
type SortOrder = 'asc' | 'desc';

export const SearchToolbar: React.FC<{
  q: string;
  onChangeQ: (q: string) => void;
  facets: TagFacetDimension[];
  selected: Record<string, string[]>; // dim_fqn -> element_fqns
  onToggleTag: (dimensionFqn: string, elementFqn: string) => void;
  sortField?: SortField;
  sortDir: SortOrder;
  onChangeSort?: (field: SortField, dir: SortOrder) => void;
  onToggleSort: () => void; // legacy - kept for backward compatibility
  viewMode?: ViewMode;
  onChangeViewMode?: (mode: ViewMode) => void;
  onSearch: () => void;
  loading?: boolean;
  onClearAll?: () => void;
  // Optional: integrate selection clear next to view mode toggle (mirrors CreativeBoard)
  selectedCompareCount?: number;
  onClearSelection?: () => void;
  typeFilterOptions?: TypeFilterOption[];
  typeFilterValue?: string;
  onChangeTypeFilter?: (value: string) => void;
  // Optional: custom right-side actions replacing the view mode toggle
  rightActions?: React.ReactNode;
}> = ({
  q,
  onChangeQ,
  facets,
  selected,
  onToggleTag,
  sortField = 'publishedAt',
  sortDir,
  onChangeSort,
  onToggleSort,
  viewMode,
  onChangeViewMode,
  onSearch,
  loading = false,
  onClearAll,
  selectedCompareCount = 0,
  onClearSelection,
  typeFilterOptions,
  typeFilterValue,
  onChangeTypeFilter,
  rightActions,
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);
  const typeFilterRef = useRef<HTMLDivElement>(null);
  const selectedCount = useMemo(() => Object.values(selected).reduce((acc, arr) => acc + (arr?.length || 0), 0), [selected]);

  const sortOptions: { value: string; label: string }[] = [
    { value: 'name-asc', label: 'Name (A-Z)' },
    { value: 'name-desc', label: 'Name (Z-A)' },
    { value: 'publishedAt-desc', label: 'Date (newest first)' },
    { value: 'publishedAt-asc', label: 'Date (oldest first)' },
  ];
  const currentSortValue = `${sortField}-${sortDir}`;
  const currentSortLabel = sortOptions.find(o => o.value === currentSortValue)?.label || 'Sort';

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setIsFilterOpen(false);
      }
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setIsSortOpen(false);
      }
      if (typeFilterRef.current && !typeFilterRef.current.contains(e.target as Node)) {
        setIsTypeFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const chips = useMemo(() => {
    const items: Array<{ key: string; label: string; dimFqn: string; elFqn: string }> = [];
    facets.forEach(dim => {
      const selectedEls = selected[dim.dimension_fqn] || [];
      selectedEls.forEach(elFqn => {
        const el = dim.elements.find(e => e.element_fqn === elFqn);
        items.push({ key: `${dim.dimension_fqn}:${elFqn}`, label: el?.name || elFqn.split('@')[1]?.split('/').pop() || elFqn, dimFqn: dim.dimension_fqn, elFqn });
      });
    });
    return items;
  }, [facets, selected]);

  return (
    <>
      <div className={boardStyles.headerControls}>
        <div className={boardStyles.controlsLeft}>
          {/* Search input with embedded button */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input
              type="text"
              value={q}
              onChange={e => onChangeQ(e.target.value)}
              placeholder="Search title and description…"
              onKeyDown={e => { if (e.key === 'Enter') onSearch(); }}
              style={{ padding: '6px 32px 6px 10px', border: '1px solid #D0D5DD', borderRadius: 6, minWidth: 260, height: 36 }}
            />
            <button
              onClick={onSearch}
              disabled={loading}
              aria-disabled={loading}
              title={loading ? 'Searching…' : 'Search'}
              style={{
                position: 'absolute',
                right: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: loading ? 'default' : 'pointer',
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                color: '#6B7280',
              }}
            >
              {loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon style={{ fontSize: 18 }} />}
            </button>
          </div>

          {typeFilterOptions && typeFilterOptions.length > 0 && onChangeTypeFilter && (
            <div className={boardStyles.filterControl} ref={typeFilterRef} style={{ width: 'auto' }}>
              <div
                className={boardStyles.filterTrigger}
                onClick={() => setIsTypeFilterOpen(v => !v)}
                role="button"
                aria-haspopup="listbox"
                aria-expanded={isTypeFilterOpen}
                style={{ minWidth: 150 }}
              >
                <div className={boardStyles.filterLabelDisplay}>
                  {typeFilterOptions.find(opt => opt.value === typeFilterValue)?.label || 'All'}
                </div>
                <KeyboardArrowDownIcon fontSize="small" className={`${boardStyles.dropdownArrow} ${isTypeFilterOpen ? boardStyles.open : ''}`} />
              </div>
              {isTypeFilterOpen && (
                <ul className={boardStyles.actionsDropdownMenu} role="listbox" aria-label="Creative type filter">
                  {typeFilterOptions.map(option => (
                    <li
                      key={option.value}
                      className={boardStyles.actionsDropdownItem}
                      role="option"
                      aria-selected={option.value === typeFilterValue}
                      onClick={() => {
                        onChangeTypeFilter(option.value);
                        setIsTypeFilterOpen(false);
                      }}
                    >
                      <span className={boardStyles.actionsDropdownItemLabel}>{option.label}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Sort Control */}
          <div className={boardStyles.filterControl} ref={sortRef}>
            <div
              className={boardStyles.filterTrigger}
              onClick={() => setIsSortOpen(v => !v)}
            >
              <SortIcon fontSize="small" />
              <div className={boardStyles.filterLabelDisplay}>
                {currentSortLabel}
              </div>
              <KeyboardArrowDownIcon fontSize="small" className={`${boardStyles.dropdownArrow} ${isSortOpen ? boardStyles.open : ''}`} />
            </div>
            {isSortOpen && (
              <div className={boardStyles.labelDropdown}>
                <ul className={boardStyles.labelsList}>
                  {sortOptions.map(opt => (
                    <li key={opt.value} className={boardStyles.labelItem}>
                      <div
                        className={`${boardStyles.filterItem} ${opt.value === currentSortValue ? boardStyles.active : ''}`}
                        onClick={() => {
                          const [field, order] = opt.value.split('-') as [SortField, SortOrder];
                          if (onChangeSort) {
                            onChangeSort(field, order);
                          } else if (order !== sortDir) {
                            onToggleSort();
                          }
                          setIsSortOpen(false);
                        }}
                      >
                        <span className={boardStyles.labelText}>{opt.label}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Tag Filter Control */}
          <div className={boardStyles.filterControl} ref={filterRef}>
            <div
              className={boardStyles.filterTrigger}
              onClick={() => setIsFilterOpen(v => !v)}
            >
              <FilterAltIcon fontSize="small" />
              <div className={boardStyles.filterLabelDisplay}>
                Filter Tags
                {selectedCount > 0 && (
                  <span className={boardStyles.activeFilterBadge}>{selectedCount}</span>
                )}
              </div>
              <KeyboardArrowDownIcon fontSize="small" className={`${boardStyles.dropdownArrow} ${isFilterOpen ? boardStyles.open : ''}`} />
            </div>

            {isFilterOpen && (
              <div className={boardStyles.labelDropdown}>
                {(facets || []).length === 0 ? (
                  <div className={boardStyles.emptyLabelsMessage}>No tags available</div>
                ) : (
                  <ul className={boardStyles.labelsList}>
                    {selectedCount > 0 && (
                      <li className={`${boardStyles.labelItem} ${boardStyles.clearFilterItem}`}>
                        <div className={boardStyles.clearFilter} onClick={onClearAll}>
                          <ClearIcon fontSize="small" className={boardStyles.clearIcon} />
                          <span className={boardStyles.clearFilterText}>Clear filter</span>
                        </div>
                      </li>
                    )}
                    {facets.map(dim => (
                      <li key={dim.dimension_fqn} className={boardStyles.labelItem}>
                        <div className={`${boardStyles.labelText} ${boardStyles.dropdownGroupHeader}`}>{dim.name}</div>
                        {dim.elements.map(el => {
                          const checked = (selected[dim.dimension_fqn] || []).includes(el.element_fqn);
                          return (
                            <div key={el.element_fqn} className={boardStyles.filterItem}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => onToggleTag(dim.dimension_fqn, el.element_fqn)}
                                />
                                <span className={boardStyles.labelText}>{el.name}</span>
                              </label>
                            </div>
                          );
                        })}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={boardStyles.controlsRight}>
          {/* Clear Selection (for compare) to the left of view switcher, like CreativeBoard */}
          {selectedCompareCount > 0 && onClearSelection && (
            <button
              className={boardStyles.clearSelectionButton}
              onClick={onClearSelection}
              title="Clear all selections"
            >
              <ClearIcon fontSize="small" />
              Clear selection
            </button>
          )}
          {/* Right side: custom actions or view mode toggle */}
          {rightActions ? rightActions : viewMode && onChangeViewMode && (
            <div className={boardStyles.viewControl}>
              <div className={boardStyles.viewModeToggle}>
                <button
                  className={`${boardStyles.viewModeButton} ${viewMode === 'list' ? boardStyles.active : ''}`}
                  onClick={() => onChangeViewMode('list')}
                  title="List view"
                >
                  <ViewStreamIcon fontSize="small" />
                </button>
                <button
                  className={`${boardStyles.viewModeButton} ${viewMode === 'grid-large' ? boardStyles.active : ''}`}
                  onClick={() => onChangeViewMode('grid-large')}
                  title="Large grid view"
                >
                  <CropSquareIcon fontSize="small" />
                </button>
                <button
                  className={`${boardStyles.viewModeButton} ${viewMode === 'grid-small' ? boardStyles.active : ''}`}
                  onClick={() => onChangeViewMode('grid-small')}
                  title="Small grid view"
                >
                  <GridViewIcon fontSize="small" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Selected filter chips row below toolbar */}
      {chips.length > 0 && (
        <div className={tagStyles.headerChipRow}>
          {chips.map(({ key, label, dimFqn, elFqn }) => (
            <button
              key={key}
              className={`${tagStyles.headerChip} ${tagStyles.headerChipSelected}`}
              onClick={() => onToggleTag(dimFqn, elFqn)}
              title={`Remove ${label}`}
            >
              <span className={tagStyles.headerChipLabel}>{`x ${label}`}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
};
